import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../lib/api";
import { useSeo } from "../components/site/Layout";
import { StatusBadge } from "./AdminLeads";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const CancelButton = ({ appt, onDone }) => {
  const cancel = async () => {
    try { await adminApi.post(`/appointments/${appt.id}/cancel`); toast.success("Rendez-vous annulé, créneau libéré"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><button type="button" className="text-brand-ink font-semibold link-u" data-testid={`appt-cancel-${appt.id}`}>Annuler</button></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Annuler ce rendez-vous ?</AlertDialogTitle>
          <AlertDialogDescription>Le rendez-vous de {appt.name} du {appt.date_label} à {appt.time} sera annulé et le créneau redeviendra réservable.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="appt-cancel-dismiss">Retour</AlertDialogCancel>
          <AlertDialogAction onClick={cancel} data-testid="appt-cancel-confirm">Confirmer l'annulation</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function AdminAppointments() {
  useSeo("Rendez-vous — admin");
  const [scope, setScope] = useState("upcoming");
  const [items, setItems] = useState(null);
  const load = useCallback(() => adminApi.get("/appointments", { params: { scope } }).then((r) => setItems(r.data)), [scope]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-appointments">
      <h1 className="text-3xl sm:text-4xl">Rendez-vous</h1>
      <div className="mt-6 flex gap-2">
        {[["upcoming", "À venir"], ["past", "Passés"], ["all", "Tous"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setScope(v)} data-testid={`appt-scope-${v}`}
            className={`rounded-full px-4 py-2 font-semibold ${scope === v ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[860px] text-left">
          <thead className="bg-slate-100 text-slate-700 text-sm uppercase tracking-wider">
            <tr><th className="p-4">Date</th><th className="p-4">Client</th><th className="p-4">Adresse</th><th className="p-4">Motif</th><th className="p-4">Statut</th><th className="p-4">Action</th></tr>
          </thead>
          <tbody>
            {items?.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`appt-row-${a.id}`}>
                <td className="p-4 whitespace-nowrap"><span className="capitalize font-semibold text-navy">{a.date_label}</span><div className="text-sm text-slate-600">{a.time}</div></td>
                <td className="p-4"><span className="font-semibold">{a.name}</span><div className="text-sm text-slate-600">{a.email}{a.phone && ` · ${a.phone}`}</div></td>
                <td className="p-4 text-sm">{a.address}, {a.postal_code}</td>
                <td className="p-4 text-sm">{a.reason}{a.message && <div className="text-slate-600">{a.message}</div>}</td>
                <td className="p-4"><StatusBadge status={a.status} testid={`appt-status-${a.id}`} /></td>
                <td className="p-4">{a.status === "confirmé" && <CancelButton appt={a} onDone={load} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items && !items.length && <p className="p-6 text-slate-600" data-testid="appointments-empty">Aucun rendez-vous.</p>}
      </div>
    </div>
  );
}
